import { useState } from "react";
import { Container } from "@/components/Container";
import { Button } from "@/components/ui/button";
import { UserProfile } from "@/components/UserProfile";
import { User, CreditCard, BarChart, LoaderCircle } from "lucide-react";
import CreditManagement from "@/components/account/credits/Credits";
import { UserAnalytics } from "@/components/account/user-analytics/UserAnalytics";
import { useTabUrlState } from "@/hooks/use-tab-url-state";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePersonasQueryNoPublic } from "@/api/personas";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Tab = {
  profile: "profile",
  analytics: "analytics",
  credits: "credits",
} as const;

type Tab = (typeof Tab)[keyof typeof Tab];
const TABS = Object.values(Tab);

const EmptyStateModal = ({ tab }: { tab: Tab }) => {
  const navigate = useNavigate();

  const modalContent = {
    analytics: {
      title: "Track and monitor your Agents Engagement",
      description:
        "Create an agent then track your interactions, usage and analytic in this tab",
      imageSrc: "/guidance-images/analytics.png",
      nextRoute: "/agents/new",
      closeRoute: "/account#profile",
    },
    credits: {
      title: "Manage Your Agent Credits",
      description:
        "Credits are used to generate content and perform agent actions. Create an agent then you can manage your credit usage and balance in this tab",
      imageSrc: "/guidance-images/credits.png",
      nextRoute: "/agents/new",
      closeRoute: "/account#profile",
    },
  };

  const content = modalContent[tab as keyof typeof modalContent];

  const handleClose = () => {
    navigate(content.closeRoute, { replace: true });
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-black/100 border-white/10 border-border md:max-w-[50%] w-[40vw] h-[70vh]">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-lg">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center h[20%] border border-white/10 rounded-lg bg-black/40">
          <img
            src={content.imageSrc}
            alt="Guidance"
            className="h-[45vh] object-contain"
          />
        </div>
        <div className="flex justify-center">
          <Button
            className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3"
            onClick={() => navigate(content.nextRoute, { replace: true })}
          >
            Create Agent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function Account() {
  const { search } = useLocation();
  const [tab] = useTabUrlState(TABS);
  const [contentKey] = useState(0);
  const { data: personas = [], isLoading: personasLoading } =
    usePersonasQueryNoPublic();

  const renderContent = () => {
    if (personasLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
        </div>
      );
    }

    if (
      personas.length === 0 &&
      (tab === Tab.analytics || tab === Tab.credits)
    ) {
      return <EmptyStateModal tab={tab} />;
    }

    return (
      <div key={contentKey} className="mt-6 mx-auto">
        {tab === Tab.profile && (
          <div className="flex flex-col gap-4 justify-center w-full max-w-[100%] mx-auto">
            <UserProfile />
          </div>
        )}

        {tab === Tab.credits && (
          <div className="flex flex-col gap-4 justify-center w-full max-w-[100%] mx-auto">
            <CreditManagement />
          </div>
        )}

        {tab === Tab.analytics && (
          <div className="flex flex-col gap-4 justify-center w-full max-w-[100%] mx-auto">
            <UserAnalytics />
          </div>
        )}
      </div>
    );
  };

  return (
    <Container>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-4 my-8">
        <Link to={{ hash: Tab.profile, search }} className="w-full sm:w-auto">
          <Button
            variant={tab === Tab.profile ? "default" : "outline"}
            className="w-full sm:min-w-[150px]"
          >
            <User className="w-4 h-4 mr-2" />
            Profile
          </Button>
        </Link>

        <Link to={{ hash: Tab.credits, search }} className="w-full sm:w-auto">
          <Button
            variant={tab === Tab.credits ? "default" : "outline"}
            className="w-full sm:min-w-[150px]"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Credits
          </Button>
        </Link>

        <Link to={{ hash: Tab.analytics, search }} className="w-full sm:w-auto">
          <Button
            variant={tab === Tab.analytics ? "default" : "outline"}
            className="w-full sm:min-w-[150px]"
          >
            <BarChart className="w-4 h-4 mr-2" />
            Analytics
          </Button>
        </Link>
      </div>

      {renderContent()}
    </Container>
  );
}
